import { useEffect, useState } from "react";
import { useSSRHook } from "ssr-hook";
import { Link } from "easy-page-router/react";
import Icon from "@mdi/react";
import { mdiPlus, mdiTimer } from "@mdi/js";

import { Footer, Header } from "../../Layout";
import { useHead } from "../../services/common";
import { Countdown } from "../../../types";
import { ContentSwitch, Loading, Page } from "../../components";
import { CountdownPreview } from "./Countdown";
import { ThemeSwitch } from "../../components/Theme";
import { LangSwitch } from "../../components/Lang";
import { plural, useLang } from "../../services/lang";
import { useApi, useHandleError } from "../../services/api";
import { useLocalHistory } from "../../services/localStorage";
import LaunchButton from "../../components/LaunchButton";
import { TimerDialog } from "./Timer";


export default function CountdownBoard() {
    const { dict } = useLang();

    useHead({
        title: dict.seo.countdownBoard.title,
        description: dict.seo.countdownBoard.description,
    });

    const [contentIndex, setContentIndex] = useState(1);

    const [showTimerDialog, setShowTimerDialog] = useState(false);

    return <Page fixed={<>
        <Header title={dict.countdown} link={`/${dict.urlCountdown}`}>
            <LangSwitch />
            <ThemeSwitch />
        </Header>
        <Footer />
    </>}>

        <h1>{dict.countdown} <span className="sub-title">{dict.countdownSubTitle}</span></h1>

        <p className="page-description">{dict.seo.countdownBoard.text}</p>

        <Link className="button-new" href={`/${dict.urlCountdown}/${dict.urlNew}`}>
            <Icon path={mdiPlus} size={1} /> {dict.newCountdown}
        </Link>

        <div className="timer-options">
            <b>{dict.timersTitle}</b>
            <div>
                <Link className="block" href={`/${dict.urlTimer}?s=30`}>
                    30 {plural(dict.second, 30)}<Icon path={mdiTimer} size={1} />
                </Link>
                <Link className="block" href={`/${dict.urlTimer}?m=1`}>
                    1 {plural(dict.minute, 1)}<Icon path={mdiTimer} size={1} />
                </Link>
                <a className="block" onClick={() => setShowTimerDialog(true)}>
                    {dict.timerSettingsOpenButton} <Icon path={mdiTimer} size={1} />
                </a>
            </div>
        </div>

        <TimerDialog
            show={showTimerDialog}
            defaultTime={{ minutes: 10, seconds: 0 }}
            onClose={() => setShowTimerDialog(false)}
        />

        <ContentSwitch
            index={contentIndex}
            onChange={setContentIndex}
            options={[{
                title: dict.countdownsLocal,
                content: <Local onLoad={() => setContentIndex(0)} />,
            }, {
                title: dict.countdownsPublic,
                content: <Public />,
            }]}
        />

    </Page>;
}

function Local({ onLoad }: { onLoad: () => void }) {
    const { dict } = useLang();

    const countdownIds = useLocalHistory("countdown");
    const [countdowns, error, isLoading, reload] = useApi.getCountdownPreviews({ countdownIds });
    useHandleError(error);

    useEffect(() => {
        if (countdowns && countdowns.length > 0) onLoad();
    }, [countdowns]);

    return <Show countdowns={countdowns} isLoading={isLoading} error={error} />;
}

function Public() {
    const { lang } = useLang();

    const [countdowns, error, isLoading, reload] = useSSRHook<Countdown[]>(`/api/countdown-previews-public?lang=${lang}`);
    useHandleError(error);

    return <Show countdowns={countdowns} isLoading={isLoading} error={error} />;
}

type ShowProps = {
    countdowns: null | Countdown[];
    error: null | string | Error;
    isLoading: boolean;
}

function Show({ countdowns, isLoading, error }: ShowProps) {
    const { dict } = useLang();
    return <>
        {isLoading && <Loading />}
        {error && <p className="error">{dict.countdownsError}</p>}
        {countdowns?.length === 0 && <p className="page-description">{dict.noCountdowns}</p>}
        {countdowns && <div className="grid countdowns">
            {countdowns.map(c => <CountdownPreview key={c._id} countdown={c} className="grid-item" />)}
        </div>}
    </>
}
